import {
  CheckoutLink,
  ConsultationApprovalRequest,
  Gender,
  MedicalCertificateRequestDto,
  RegenerateMedicalCertificateRequest,
  TokenProcessingResult,
  VerificationResponse,
} from '@/lib/apis/types'
import { CertificateVerificationUrl } from '@/lib/apis/routes'
import { MutationKeys } from '@/lib/apis/service'
import { MedicalCertificate } from '@/lib/questionnaires/MedicalCertificate'
import WebRequest from '@/lib/apis/WebRequest'
import { ApiClient } from '@/lib/apis/clients/ApiClient'
import { isHttpError } from '@/lib/apis/error'
import qs from 'qs'

export namespace ApproveMedicalCertificate {
  type Params = {
    request: ConsultationApprovalRequest
    reference: string
  }

  function api({ request, reference }: Params): WebRequest {
    return WebRequest.POST(`/medical-certificates/${reference}/approve`).body(
      request,
    )
  }

  export function mutation(client: ApiClient, params: Omit<Params, 'request'>) {
    const { reference } = params

    return {
      mutationFn: (request: ConsultationApprovalRequest) =>
        client.executeEmpty(api({ ...params, request })),
      mutationKey: MutationKeys.ApproveMedicalCertificate(reference),
    }
  }
}

export namespace GetMedicalCertificateVerification {
  type Params = {
    reference: string
    signature: string
  }

  export async function api(
    client: ApiClient,
    { reference, signature }: Params,
  ): Promise<VerificationResponse> {
    const url = CertificateVerificationUrl(reference)
    const search = qs.stringify({ reference, signature })
    return client.execute(WebRequest.GET(`${url}?${search}`))
  }
}

export namespace ProcessPostCheckoutToken {
  type Params = {
    token: string
  }

  export async function api(
    client: ApiClient,
    { token }: Params,
  ): Promise<TokenProcessingResult> {
    const url = '/payment/process'
    const search = qs.stringify({ token })
    return client.execute(WebRequest.POST(`${url}?${search}`))
  }

  export function isExpiredTokenError(error: unknown): boolean {
    return (
      isHttpError(error) &&
      error.response?.error === 'EXPIRED_PAYMENT_TOKEN'
    )
  }
}

export namespace RequestMedicalCertificate {
  type Params = {
    authenticated: boolean
    values: MedicalCertificate.QuestionnaireValues
  }

  function api({ authenticated, values }: Params): WebRequest {
    const {
      firstName,
      lastName,
      gender,
      email,
      mobile,
      dob,
      formattedAddress,
      suburb,
      state,
      postcode,
      onDate,
      toDate,
      mcType,
      mcFor,
      symptoms,
      mcSymptom,
      mcSeeGpAgreement,
      mcInlandAgreement,
      mcTnC,
      requiresDoctorVisit,
      hasSeriousSymptoms,
      mcCarerFor,
      childHasSeriousSymptoms,
      hasPastMedicalConditions,
      pastMedicalConditions,
      concessionNumber,
      entitlementNumber,
      isAbOrTorIslander,
    } = values

    const dto = {
      certificate: {
        onDate,
        toDate,
        mcType,
        mcFor,
        mcSymptom,
        mcCarerFor,
        childHasSeriousSymptoms,
        mcSeeGpAgreement: mcSeeGpAgreement === 'Yes',
        mcInlandAgreement: mcInlandAgreement === 'Yes',
        mcTnC: mcTnC === 'Yes',
        requiresDoctorVisit: requiresDoctorVisit === 'Yes',
        hasSeriousSymptoms: hasSeriousSymptoms === 'Yes',
        hasPastMedicalConditions: hasPastMedicalConditions === 'Yes',
        pastMedicalConditions:
          hasPastMedicalConditions === 'Yes' ? pastMedicalConditions : null,
        symptoms,
      },
      ...(!authenticated
        ? {
            profile: {
              firstName,
              lastName,
              gender: gender as Gender,
              email,
              mobile,
              dob,
              formattedAddress,
              suburb,
              state,
              postcode,
              type: 'patient',
              concessionNumber,
              entitlementNumber,
              isAbOrTorIslander,
            },
            tokens: {
              mobile: values.mobileVerificationToken ?? null,
              email: null,
            },
          }
        : {}),
      scheduledSlot: null,
    } as MedicalCertificateRequestDto

    return WebRequest.POST('/medical-certificates').body(dto)
  }

  export function mutation(
    client: ApiClient,
    { authenticated }: Omit<Params, 'values'>,
  ) {
    return {
      mutationFn: ({ values }: Pick<Params, 'values'>) =>
        client.execute<CheckoutLink>(api({ authenticated, values })),
      mutationKey: MutationKeys.RequestMedicalCertificate(),
    }
  }
}

export namespace RegenerateMedicalCertificate {
  async function api(
    client: ApiClient,
    reference: string,
    request: RegenerateMedicalCertificateRequest,
  ): Promise<void> {
    const req = WebRequest.POST(
      `/medical-certificates/${reference}/regenerate`,
    ).body(request)

    return client.executeEmpty(req)
  }

  export function mutation(client: ApiClient, reference: string) {
    return {
      mutationFn: (request: RegenerateMedicalCertificateRequest) =>
        api(client, reference, request),
      mutationKey: MutationKeys.ApproveMedicalCertificate(reference),
    }
  }
}
